import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  DialogActions,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import AddUserPage from "../Adding/addUsers/addusers";
import EditUsersPage from "../Editing/editUsers/edituser";
import { endpoints } from "../../services/endpoint"; 
import { axiosInstance } from "../../services/axiosInstance"; // Importing named exportadjust path as needed
import "./userdisplay.css";
import UploadFileModal from "../Adding/addUsers/uploadmodal";

const UserPage = ({
  users = [],
  onDelete,
  onAdd,
}) => {
  const [userState, setUserState] = useState(users);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedArn, setCopiedArn] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUsers, setEditingUsers] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteActionClicked, setDeleteActionClicked] = useState(null); // 'delete' or 'cancel'
  const [deletingUserLogin, setDeletingUserLogin] = useState(null); // State to track which user is being deleted
  
  const instanceAlias = localStorage.getItem("instanceName");
  const EncryptedInstanceId = localStorage.getItem("EncryptedId");
  const email = localStorage.getItem("Email");
  const instanceId = localStorage.getItem("InstanceId");
  const customerAWSAccountId = localStorage.getItem("CustomerAWSAccountId");
  const region = localStorage.getItem("Region");

  useEffect(() => {
    setUserState(users);
  }, [users]);

  const handleCopy = (arn) => {
    navigator.clipboard.writeText(arn).then(() => {
      setCopiedArn(arn);
      setTimeout(() => setCopiedArn(""), 2000);
    });
  };

  const handleBulkProfileAdded = (updatedList) => {
    setUserState(updatedList || []);
    setShowAddForm(false);
    setShowUploadModal(false); // ✅ close upload modal after update
  };

  const handleProfileAdded = (updatedList) => {
    setUserState(updatedList || []);
    setShowAddForm(false);
  };

  const handleUsersUpdated = (updatedList) => {
    setUserState(updatedList || []);
    setEditingUsers(null);
  };

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return userState.filter((user) => {
      // Collect all searchable values
      const valuesToSearch = [
        user.FullName,
        user.ACWTimeout,
        user.PhoneType,
        user.DeskPhone,
        user.Login,
        user.AutoAccept ? "Yes" : "No",
        user.RoutingProfile,
        user.ARN,
        ...(user.SecurityProfiles || []), // array of strings
        ...(user.Tags
          ? Object.entries(user.Tags).map(([k, v]) => `${k}:${v}`)
          : []), // convert tags object into strings
      ];

      // Check if any field matches the term
      return valuesToSearch.some((val) =>
        String(val ?? "").toLowerCase().includes(term)
      );
    });
  }, [searchTerm, userState]);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
    setDeleteActionClicked(null); // Reset action state when opening modal
    setDeletingUserLogin(null); // Reset deleting user state
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    setDeleteActionClicked('delete'); // Mark delete as clicked
    setLoading(true);
    setDeletingUserLogin(userToDelete.Login); // Track which user is being deleted

    const requestPayload = {
      action: "delete",
      ID: EncryptedInstanceId,
      username: userToDelete.Login,
      awsAccount: customerAWSAccountId, // used your second awsAccount value as source of truth
      InstanceAlias: instanceAlias,
      Email: email,
      region,
    };

    try {
      await axiosInstance.post(endpoints.addUser.newuser, requestPayload);
      const updatedList = userState.filter(
        (u) => u.Login !== userToDelete.Login
      );
      setUserState(updatedList);
      setDeleteModalOpen(false);
      setUserToDelete(null);
      setDeleteActionClicked(null); // Reset state
      setDeletingUserLogin(null); // Reset deleting user
    } catch (error) {
      console.error("Delete failed:", error);
      setDeletingUserLogin(null); // Reset on error too
    }
    
    setLoading(false);
  };

  const handleCancelDelete = () => {
    setDeleteActionClicked('cancel'); // Mark cancel as clicked
    
    // Add a small delay to show the visual feedback before closing
    setTimeout(() => {
      setDeleteModalOpen(false);
      setUserToDelete(null);
      setDeleteActionClicked(null); // Reset state
      setDeletingUserLogin(null); // Reset deleting user
    }, 300);
  };

  if (showAddForm) {
    return (
      <AddUserPage
        onClose={() => setShowAddForm(false)}
        onProfileAdded={handleProfileAdded}
        onBulkProfileAdded={handleBulkProfileAdded}
      />
    );
  }

  if (editingUsers) {
    return (
      <EditUsersPage
        users={editingUsers}
        onClose={() => setEditingUsers(null)}
        onUpdate={handleUsersUpdated}
      />
    );
  }

  return (
      <Box p={3}>
      <Typography variant="h5" className="phone-number-title">Amazon Connect Users</Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          placeholder="Search by Full Name or Login"
          className="search-textfield"
          fullWidth
          sx={{ mb: 2, mr: 2 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" className="adduser-button" startIcon={<AddIcon />} onClick={() => setShowAddForm(true)}>
          Add User
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {filteredUsers.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Login</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>ACW Timeout</TableCell>
              <TableCell>Phone Type</TableCell>
              <TableCell>Desk Phone</TableCell>
              <TableCell>Auto Accept</TableCell>
              <TableCell>Routing Profile</TableCell>
              <TableCell>Security Profiles</TableCell>
              <TableCell>ARN</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setEditingUsers(user);
                    }}
                    style={{
                      textDecoration: "underline",
                      color: "#1976d2",
                      cursor: "pointer",
                    }}
                  >
                    {user.Login}
                  </a>
                </TableCell>
                <TableCell>{user.FullName || "-"}</TableCell>
                <TableCell>{user.ACWTimeout || "-"}</TableCell>
                <TableCell>{user.PhoneType || "-"}</TableCell>
                <TableCell>{user.DeskPhone || "-"}</TableCell>
                <TableCell>{user.AutoAccept ? "Yes" : "No"}</TableCell>
                <TableCell>{user.RoutingProfile || "-"}</TableCell>
                <TableCell>
                  {user.SecurityProfiles?.length > 0
                    ? user.SecurityProfiles.map((profile, i) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{
                            backgroundColor: "#e0e0e0",
                            borderRadius: 1,
                            px: 1,
                            display: "inline-block",
                            mr: 0.5,
                          }}
                        >
                          {profile}
                        </Typography>
                      ))
                    : "-"}
                </TableCell>
                <TableCell>
                  <Tooltip title={copiedArn === user.ARN ? "Copied!" : "Copy ARN"}>
                    <IconButton onClick={() => handleCopy(user.ARN)}>
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {user.Tags && typeof user.Tags === "object"
                    ? Object.entries(user.Tags).map(([key, value], i) => (
                        <Chip
                          key={i}
                          label={`${key}: ${value}`}
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      ))
                    : "-"}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(user)}
                    disabled={deletingUserLogin === user.Login}
                  >
                    {deletingUserLogin === user.Login ? (
                      <CircularProgress size={18} color="error" />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography>No users found.</Typography>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={null} disableEscapeKeyDown>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{" "}
          <strong>{userToDelete?.FullName || userToDelete?.Login}</strong>?
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelDelete}
            disabled={deleteActionClicked === 'delete'} // Disabled when delete is clicked
            sx={{ 
              opacity: deleteActionClicked === 'delete' ? 0.3 : 1,
              transition: 'opacity 0.2s ease-in-out',
              pointerEvents: deleteActionClicked === 'delete' ? 'none' : 'auto',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteActionClicked === 'cancel'} // Disabled when cancel is clicked
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ 
              opacity: deleteActionClicked === 'cancel' ? 0.3 : 1,
              transition: 'opacity 0.2s ease-in-out',
              pointerEvents: deleteActionClicked === 'cancel' ? 'none' : 'auto',
            }}
          >
            {loading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPage;